import { Link, useRouterState } from "@tanstack/react-router";

/**
 * Site-wide visible breadcrumb trail, derived from the current pathname.
 *
 * Routes that already render their own trail opt out here so a page can never
 * show both its local trail and this shared trail. BreadcrumbList JSON-LD is
 * metadata and remains independently owned by each route's head().
 */
const SKIP_PREFIXES = ["/guides/", "/pals/"];
const SKIP_PATHS = new Set([
  "/boss-route",
  "/capture-rates",
  "/card-checker",
  "/cards",
  "/deck-builder",
  "/eidrolon",
  "/tcg-tools",
  "/technology-tree",
]);

/** URL prefixes that have no page of their own (dynamic-only parents). */
const NON_ROUTE_PARENTS = new Set(["/pull", "/binder-share", "/shared-binder", "/tcg-index-snapshot", "/decks"]);


const LABELS: Record<string, string> = {
  pals: "Pals",
  cards: "TCG Cards",
  "card-checker": "Card Checker",
  "tcg-tools": "TCG Tools",
  "tcg-price-index": "TCG Price Index",
  "deck-builder": "Deck Builder",
  "capture-rates": "Capture Rates",
  "technology-tree": "Technology Tree",
  "team-builder": "Team Builder",
  "tier-list": "Tier List",
  "booster-pack": "Booster Pack",
  "pack-opening": "Pack Opening",
  palsino: "Palsino",
  rewards: "Free Packs",
  binder: "Binder",
  trades: "Card Trading",
  "want-list": "Want List Board",
  players: "Player Profiles",
  eggs: "Eggs",
  bosses: "Bosses",
  damage: "Damage Calculator",
  breeding: "Breeding Calculator",
  mounts: "Mount Speeds",
  elements: "Elemental Chart",
  map: "Interactive Map",
  favorites: "Favorites",
  about: "About",
  contact: "Contact",
  privacy: "Privacy Policy",
  terms: "Terms of Service",
  account: "Account",
  auth: "Sign in",
  eidrolon: "Eidrolon",
};

function label(segment: string) {
  return (
    LABELS[segment] ??
    segment.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

export function Breadcrumbs() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  if (pathname === "/") return null;
  if (SKIP_PATHS.has(pathname.replace(/\/+$/, ""))) return null;
  if (SKIP_PREFIXES.some((p) => pathname.startsWith(p))) return null;

  const segments = pathname.replace(/\/+$/, "").split("/").filter(Boolean);
  if (segments.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="mx-auto max-w-7xl px-4 sm:px-6 pt-3">
      <ol className="flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
        <li>
          <Link to="/" className="hover:text-foreground">Home</Link>
        </li>
        {segments.map((seg, i) => {
          const last = i === segments.length - 1;
          const href = `/${segments.slice(0, i + 1).join("/")}`;
          // Some parents exist only as URL prefixes (/pull/$code, /binder-share/$token):
          // linking them would point crawlers at a 404.
          const linkable = !last && !NON_ROUTE_PARENTS.has(href);
          return (
            <li key={href} className="flex items-center gap-1.5">
              <span aria-hidden="true">/</span>
              {linkable ? (
                <Link to={href as never} className="hover:text-foreground">{label(seg)}</Link>
              ) : (
                <span className={last ? "text-foreground" : undefined}>{label(seg)}</span>
              )}
            </li>
          );
        })}

      </ol>
    </nav>
  );
}
